import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Scan, Upload, FileText, Check, Trash2, RefreshCw, ArrowRight, Settings, Eye, Pencil, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { confirmAsync } from '@/lib/confirm';

function OCRTickets({ user }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Dialog de revisión
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editedTicket, setEditedTicket] = useState(null);
  const [converting, setConverting] = useState(false);
  const [proveedores, setProveedores] = useState([]);

  useEffect(() => {
    loadTickets();
    loadProveedores();
  }, []);

  const loadTickets = async () => {
    try {
      const response = await axiosInstance.get('/ocr/tickets');
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error cargando tickets:', error);
      toast.error('Error al cargar los tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadProveedores = async () => {
    try {
      const response = await axiosInstance.get('/proveedores');
      setProveedores(response.data.proveedores || []);
    } catch (error) {
      console.error('Error cargando proveedores:', error);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Tipo de archivo no válido. Usa JPG, PNG o PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande (máx 10MB)');
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleProcessOCR = async () => {
    if (!selectedFile) return;

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axiosInstance.post('/ocr/process-ticket', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Ticket procesado correctamente con OCR');
      
      if (response.data.proveedor_creado) {
        toast.info(
          `Nuevo proveedor creado: "${response.data.data?.proveedor_nombre}". Pendiente de revisión en Proveedores.`,
          { duration: 5000 }
        );
      }

      setSelectedFile(null);
      setPreview(null);
      loadTickets();
    } catch (error) {
      console.error('Error procesando OCR:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el ticket');
    } finally {
      setProcessing(false);
    }
  };

  const openReviewDialog = (ticket) => {
    setSelectedTicket(ticket);
    setEditedTicket({
      proveedor_nombre: ticket.proveedor_nombre || '',
      proveedor_nif: ticket.proveedor_nif || '',
      proveedor_id: ticket.proveedor_id || '',
      numero_factura: ticket.numero_factura || '',
      fecha_emision: ticket.fecha_emision ? ticket.fecha_emision.split('T')[0] : '',
      concepto: ticket.concepto || '',
      subtotal: ticket.subtotal || 0,
      tax: ticket.tax || 0,
      total: ticket.total || 0,
      items: ticket.items || []
    });
    setShowReviewDialog(true);
  };

  const handleConvertToInvoice = async () => {
    if (!selectedTicket || !editedTicket) return;

    setConverting(true);
    try {
      // Primero actualizar el ticket con los datos editados
      await axiosInstance.put(`/ocr/tickets/${selectedTicket.id}`, editedTicket);
      
      // Luego convertir a factura
      await axiosInstance.post(`/ocr/tickets/${selectedTicket.id}/convert`);
      toast.success('Ticket convertido a factura recibida');
      setShowReviewDialog(false);
      loadTickets();
    } catch (error) {
      console.error('Error convirtiendo ticket:', error);
      toast.error(error.response?.data?.detail || 'Error al convertir el ticket');
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async (ticketId) => {
    if (!(await confirmAsync('¿Estás seguro de eliminar este ticket?'))) return;

    try {
      await axiosInstance.delete(`/ocr/tickets/${ticketId}`);
      toast.success('Ticket eliminado correctamente');
      loadTickets();
    } catch (error) {
      console.error('Error eliminando ticket:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el ticket');
    }
  };

  const handleProveedorChange = (proveedorId) => {
    const proveedor = proveedores.find(p => p.id === proveedorId);
    if (proveedor) {
      setEditedTicket({
        ...editedTicket,
        proveedor_id: proveedor.id,
        proveedor_nombre: proveedor.nombre_comercial,
        proveedor_nif: proveedor.nif
      });
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...editedTicket.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalcular subtotal
    const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const tax = subtotal * 0.21; // Assuming 21% IVA
    
    setEditedTicket({
      ...editedTicket,
      items: newItems,
      subtotal: subtotal,
      tax: tax,
      total: subtotal + tax
    });
  };

  const addItem = () => {
    setEditedTicket({
      ...editedTicket,
      items: [...editedTicket.items, { description: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = editedTicket.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const tax = subtotal * 0.21;
    
    setEditedTicket({
      ...editedTicket,
      items: newItems,
      subtotal: subtotal,
      tax: tax,
      total: subtotal + tax
    });
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy', { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando tickets...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Scan className="h-8 w-8 text-purple-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
                Contabilidad C10
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/contabilidad/fiscal')}
                variant="outline"
                size="sm"
                className="bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/asientos')}
                variant="outline"
                size="sm"
                className="bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                Asientos
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/facturas-recibidas')}
                variant="outline"
                size="sm"
                className="bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                Fact. Recibidas
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/libros')}
                variant="outline"
                size="sm"
                className="bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                Libros
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/config')}
                variant="outline"
                size="sm"
                className="bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-base text-gray-600">
            OCR - Digitaliza tickets y facturas automáticamente con OpenAI Vision
          </p>
        </div>

        {/* Upload Area */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-purple-600" />
              Subir Ticket
            </CardTitle>
            <CardDescription>
              Arrastra una imagen o PDF de un ticket, o haz click para seleccionar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {!selectedFile ? (
                <div>
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Arrastra tu ticket aquí o haz click para seleccionar
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Formatos: JPG, PNG, PDF (máx 10MB)
                  </p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />
                  <label htmlFor="file-upload">
                    <Button asChild variant="outline" className="bg-white text-gray-700 border-gray-300 hover:bg-gray-100 cursor-pointer">
                      <span>Seleccionar Archivo</span>
                    </Button>
                  </label>
                </div>
              ) : (
                <div>
                  {preview && (
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-64 mx-auto mb-4 rounded"
                    />
                  )}
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={handleProcessOCR}
                      disabled={processing}
                      variant="default"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {processing ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <Scan className="h-4 w-4 mr-2" />
                          Procesar con OCR
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedFile(null);
                        setPreview(null);
                      }}
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Lista de Tickets */}
        <Card>
          <CardHeader>
            <CardTitle>Tickets Procesados</CardTitle>
            <CardDescription>
              Historial de tickets digitalizados con OCR
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tickets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p>No hay tickets procesados</p>
                <p className="text-sm mt-2">Sube tu primer ticket para empezar</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">
                            {ticket.numero_factura || ticket.filename}
                          </h3>
                          {ticket.convertido_a_factura && (
                            <Badge className="bg-green-100 text-green-800">
                              <Check className="h-3 w-3 mr-1" />
                              Convertido
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Proveedor:</span>{' '}
                            <span className="font-medium">
                              {ticket.proveedor_nombre || 'No especificado'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Fecha:</span>{' '}
                            <span className="font-medium">
                              {formatDate(ticket.fecha_emision)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">NIF:</span>{' '}
                            <span className="font-medium">
                              {ticket.proveedor_nif || '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Total:</span>{' '}
                            <span className="font-bold text-lg">
                              {formatCurrency(ticket.total)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {!ticket.convertido_a_factura ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReviewDialog(ticket)}
                              className="text-blue-600 border-blue-600 hover:bg-blue-50"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Revisar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(ticket.id)}
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate('/contabilidad/facturas-recibidas')}
                          >
                            Ver Factura
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog de Revisión */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-purple-600" />
                Revisar Ticket antes de Convertir
              </DialogTitle>
              <DialogDescription>
                Revisa y edita los datos extraídos por el OCR antes de crear la factura recibida.
              </DialogDescription>
            </DialogHeader>

            {editedTicket && (
              <div className="space-y-6 mt-4">
                {/* Datos del Proveedor */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium mb-3 text-gray-700">Datos del Proveedor</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Seleccionar Proveedor Existente</Label>
                      <Select 
                        value={editedTicket.proveedor_id || ''} 
                        onValueChange={handleProveedorChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {proveedores.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.nombre_comercial} ({p.nif})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Nombre del Proveedor</Label>
                      <Input
                        value={editedTicket.proveedor_nombre}
                        onChange={(e) => setEditedTicket({...editedTicket, proveedor_nombre: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>NIF</Label>
                      <Input
                        value={editedTicket.proveedor_nif}
                        onChange={(e) => setEditedTicket({...editedTicket, proveedor_nif: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Datos de la Factura */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium mb-3 text-gray-700">Datos de la Factura</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Número de Factura</Label>
                      <Input
                        value={editedTicket.numero_factura}
                        onChange={(e) => setEditedTicket({...editedTicket, numero_factura: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Fecha de Emisión</Label>
                      <Input
                        type="date"
                        value={editedTicket.fecha_emision}
                        onChange={(e) => setEditedTicket({...editedTicket, fecha_emision: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Concepto</Label>
                      <Input
                        value={editedTicket.concepto}
                        onChange={(e) => setEditedTicket({...editedTicket, concepto: e.target.value})}
                        placeholder="Descripción general"
                      />
                    </div>
                  </div>
                </div>

                {/* Items/Líneas */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-700">Líneas del Ticket</h3>
                    <Button size="sm" variant="outline" onClick={addItem}>
                      <Plus className="h-3 w-3 mr-1" />
                      Añadir Línea
                    </Button>
                  </div>
                  
                  {editedTicket.items && editedTicket.items.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40%]">Descripción</TableHead>
                          <TableHead className="text-right">Cantidad</TableHead>
                          <TableHead className="text-right">Precio Unit.</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editedTicket.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Input
                                value={item.description || ''}
                                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.quantity || 1}
                                onChange={(e) => {
                                  const qty = parseFloat(e.target.value) || 0;
                                  updateItem(idx, 'quantity', qty);
                                  updateItem(idx, 'total', qty * (item.unit_price || 0));
                                }}
                                className="h-8 text-right w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price || 0}
                                onChange={(e) => {
                                  const price = parseFloat(e.target.value) || 0;
                                  updateItem(idx, 'unit_price', price);
                                  updateItem(idx, 'total', (item.quantity || 1) * price);
                                }}
                                className="h-8 text-right w-24"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.total || 0}
                                onChange={(e) => updateItem(idx, 'total', parseFloat(e.target.value) || 0)}
                                className="h-8 text-right w-24 font-medium"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeItem(idx)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No hay líneas de detalle</p>
                  )}
                </div>

                {/* Totales */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-medium mb-3 text-purple-700">Totales</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Subtotal (sin IVA)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editedTicket.subtotal}
                        onChange={(e) => {
                          const subtotal = parseFloat(e.target.value) || 0;
                          setEditedTicket({
                            ...editedTicket,
                            subtotal: subtotal,
                            total: subtotal + (editedTicket.tax || 0)
                          });
                        }}
                        className="font-medium"
                      />
                    </div>
                    <div>
                      <Label>IVA</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editedTicket.tax}
                        onChange={(e) => {
                          const tax = parseFloat(e.target.value) || 0;
                          setEditedTicket({
                            ...editedTicket,
                            tax: tax,
                            total: (editedTicket.subtotal || 0) + tax
                          });
                        }}
                        className="font-medium"
                      />
                    </div>
                    <div>
                      <Label>TOTAL</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editedTicket.total}
                        onChange={(e) => setEditedTicket({...editedTicket, total: parseFloat(e.target.value) || 0})}
                        className="font-bold text-lg bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleConvertToInvoice}
                disabled={converting}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {converting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Convirtiendo...
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Convertir a Factura
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default OCRTickets;
